import { Box, HStack, Icon, Spinner, Text, Textarea, useToast } from '@chakra-ui/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Check } from 'lucide-react';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { AssessmentSubmissionWithAnswers } from '@duke-hq/svc-api/lib/assessment/types';
import { SlideKind } from '@duke-hq/svc-api-db';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import PreviousAttempts from './PreviousAttempts';
import type { FormattedSlide } from '../components/types';
import { useMarkingContext } from './context/MarkingContext';
import { PreviousFeedbackPanel } from './PreviousFeedbackPanel';
import { testIds } from './testIds';
import { markingStatus } from './utils';

const AUTO_SAVE_DELAY_MS = 500;

export function QuestionResult({
  answer,
  slide,
}: {
  answer: AssessmentSubmissionWithAnswers;
  slide: FormattedSlide;
}) {
  const { assessmentSubmission, currentAnswerFeedback } = useMarkingContext();
  const hasAssessed = assessmentSubmission.assessed_at !== null;

  const optionFeedback = slide?.question?.options?.find(
    (option) => option.id === answer.answerId[0],
  )?.feedback;

  const status = markingStatus(slide.correct);
  const isFormWithIncorrectFields =
    slide.kind === SlideKind.FORM &&
    answer.formFieldAnswers.some(({ correct }) => correct === false);
  const shouldShowRevisionFeedback = status === 'REVISION' || isFormWithIncorrectFields;
  const canWriteFeedback = shouldShowRevisionFeedback && !hasAssessed && optionFeedback == null;
  const answerKey =
    answer.unitAssessmentEnrolmentId !== null
      ? `${answer.originalAnswerId}:${answer.unitAssessmentEnrolmentId}`
      : String(answer.originalAnswerId);
  const feedbackReady =
    answer.unitAssessmentEnrolmentId === null || currentAnswerFeedback !== undefined;

  return (
    <Box>
      {feedbackReady ? (
        <FeedbackEditor
          key={`${answerKey}:${status}`}
          answer={answer}
          slide={slide}
          initialFeedback={currentAnswerFeedback?.feedback ?? ''}
          canWriteFeedback={canWriteFeedback}
          optionFeedback={optionFeedback}
        />
      ) : (
        <PreviousSubmission answer={answer} slide={slide} />
      )}
    </Box>
  );
}

function PreviousSubmission({
  answer,
  slide,
}: {
  answer: AssessmentSubmissionWithAnswers;
  slide: FormattedSlide;
}) {
  const { assessmentSubmission } = useMarkingContext();

  if (
    answer.unitAssessmentEnrolmentId == null ||
    !assessmentSubmission.previousAssessmentSubmissions
  ) {
    return null;
  }

  return (
    <Box bg="bg.surface">
      <PreviousAttempts
        options={slide.question?.options}
        questionId={answer.questionId}
        previousAssessmentSubmissions={assessmentSubmission.previousAssessmentSubmissions}
        unitAssessmentEnrolmentId={answer.unitAssessmentEnrolmentId}
        slide={slide}
      />
    </Box>
  );
}

function FeedbackEditor({
  answer,
  slide,
  initialFeedback,
  canWriteFeedback,
  optionFeedback,
}: {
  answer: AssessmentSubmissionWithAnswers;
  slide: FormattedSlide;
  initialFeedback: string;
  canWriteFeedback: boolean;
  optionFeedback: string | null | undefined;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const [feedback, setFeedback] = useState(() => initialFeedback);
  const [hasSavedFeedback, setHasSavedFeedback] = useState(() => initialFeedback.trim() !== '');
  const lastSavedRef = useRef(initialFeedback);

  const { mutate: submitFeedback, isPending } =
    trpcAdmin.assessments.submitAnswerFeedback.useMutation({
      async onSuccess(savedFeedback, variables) {
        utils.assessments.getAnswerFeedbackById.setData(
          {
            answerId: variables.answerId,
            unitAssessmentEnrolmentId: variables.unitAssessmentEnrolmentId,
          },
          savedFeedback,
        );

        const nowHasFeedback = variables.feedback.trim() !== '';

        if (nowHasFeedback !== hasSavedFeedback) {
          await utils.assessments.getById.invalidate();
        }

        setHasSavedFeedback(nowHasFeedback);
      },
      onError(error) {
        toast({
          status: 'error',
          title: t`Couldn't save feedback`,
          description: error.message,
        });
      },
    });

  const onFeedbackInputChange = useCallback(
    (input: string | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setFeedback(typeof input === 'string' ? input : (input?.target?.value ?? ''));
    },
    [],
  );

  const onInsertFeedback = useCallback((inserted: string) => {
    setFeedback((current) => (current.trim() === '' ? inserted : `${current}\n\n${inserted}`));
  }, []);

  useEffect(() => {
    const unitAssessmentEnrolmentId = answer.unitAssessmentEnrolmentId;

    if (
      !canWriteFeedback ||
      unitAssessmentEnrolmentId == null ||
      feedback === lastSavedRef.current
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      lastSavedRef.current = feedback;
      submitFeedback({
        unitAssessmentEnrolmentId,
        assessmentSubmissionId: answer.assessmentSubmissionId,
        answerId: answer.originalAnswerId,
        feedback,
      });
    }, AUTO_SAVE_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [
    feedback,
    canWriteFeedback,
    answer.assessmentSubmissionId,
    answer.originalAnswerId,
    answer.unitAssessmentEnrolmentId,
    submitFeedback,
  ]);

  return (
    <>
      <Box bg="bg.surface" px={6} py={5} position="sticky" top={0} zIndex={1} boxShadow="sm">
        <Text fontSize="sm" fontWeight="semibold" mb={3}>
          <Trans>Feedback for the student</Trans>
        </Text>

        <Textarea
          data-testid={testIds.feedbackTextarea}
          value={optionFeedback ?? feedback}
          placeholder={
            canWriteFeedback
              ? t`Write revision feedback response`
              : t`Pick Revision or Correct above. Feedback only goes out for Revision.`
          }
          size="sm"
          minH={pxToRem(120)}
          isDisabled={!canWriteFeedback}
          onChange={onFeedbackInputChange}
        />

        <Box mt={2} data-testid={testIds.feedbackSaveStatus}>
          {isPending ? (
            <HStack gap={1}>
              <Spinner boxSize={iconSize.sm} color="fg.muted" />
              <Text fontSize="xs" color="fg.muted">
                <Trans>Saving</Trans>
              </Text>
            </HStack>
          ) : hasSavedFeedback ? (
            <HStack gap={1}>
              <Icon as={Check} boxSize={iconSize.sm} color="green.500" />
              <Text fontSize="xs" color="fg.muted">
                <Trans>Feedback saved</Trans>
              </Text>
            </HStack>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              <Trans>Auto-saves as you type</Trans>
            </Text>
          )}
        </Box>
      </Box>

      <PreviousSubmission answer={answer} slide={slide} />

      {canWriteFeedback && answer.unitAssessmentEnrolmentId !== null ? (
        <PreviousFeedbackPanel
          questionId={answer.questionId}
          answerId={answer.originalAnswerId}
          unitAssessmentEnrolmentId={answer.unitAssessmentEnrolmentId}
          onEditAndInsert={onInsertFeedback}
        />
      ) : null}
    </>
  );
}
